import { useRef, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, X } from "lucide-react";
import { superUploadAsset, type AssetKind } from "../../lib/api";

// Restricciones del backend (apps/api/src/lib/r2.ts). Mantener en sync.
export const ASSET_MAX_MB = 2;
export const ASSET_ACCEPTED = "image/png,image/jpeg,image/webp,image/svg+xml";

// Recomendaciones de dimensiones por kind. No son obligatorias —
// solo guían al usuario para que no suba imágenes demasiado chicas o
// con la proporción equivocada.
export const ASSET_TIPS: Record<AssetKind, { recommended: string; tip: string }> = {
  logo: {
    recommended: "PNG con fondo transparente, ~600 × 200 px",
    tip: "Se muestra en el header. Conviene PNG transparente para que se vea limpio sobre cualquier fondo.",
  },
  "bg-landscape": {
    recommended: "JPG 3840 × 2160 px (4K 16:9) o WebP",
    tip: "Fondo cuando la pantalla está horizontal (TV apaisada). Para TV 4K 65\" subí 3840×2160 — JPG calidad 85 pesa ~400-700 KB, sin pixelado. Centro despejado: arriba va el logo y abajo los botones.",
  },
  "bg-portrait": {
    recommended: "JPG 2160 × 3840 px (4K 9:16) o WebP",
    tip: "Fondo cuando la pantalla está vertical (TV rotada). Para TV 4K 65\" subí 2160×3840 — JPG calidad 85 pesa ~400-700 KB. Centro despejado: arriba va el logo y abajo los botones.",
  },
  campana: {
    recommended: "PNG/JPG 1200 × 600 px",
    tip: "Banner de la campaña activa (Mundial, Pascua, etc.). Se ve grande en el centro de la landing.",
  },
  "icon-ruleta": {
    recommended: "PNG cuadrado con fondo transparente, ~256 × 256 px",
    tip: "Ícono dentro del botón Ruleta de la landing. Pelota de fútbol para Mundial, huevo para Pascua, etc. Si no subís, se muestra el ícono por defecto (dados).",
  },
  "icon-raspadita": {
    recommended: "PNG cuadrado con fondo transparente, ~256 × 256 px",
    tip: "Ícono dentro del botón Raspadita de la landing. Si no subís, se muestra el ícono por defecto (goma de borrar).",
  },
  // Migration 033: ícono del botón Memoria.
  "icon-memoria": {
    recommended: "PNG cuadrado con fondo transparente, ~256 × 256 px",
    tip: "Ícono dentro del botón Memoria de la landing. Si no subís, se muestra el ícono por defecto (cerebro).",
  },
  // Migration 022: 6 imágenes del tablero del juego memoria. SVG ideal
  // (escala infinita, sin pixelar en TV 65" 4K). PNG 1024×1024 si no hay
  // SVG. La recomendación vieja de 256×256 quedó corta para 4K vertical
  // (la pantalla tiene 2160 px de ancho, el tablero ocupa ~70% del ancho
  // = ~1500 device px → casilla ~490 device px; 256 queda en el límite).
  "memoria-1": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 1 de las 6 pares del tablero. SVG se ve perfecto en TV 4K. Cualquier ícono/logo/bandera/símbolo cuadrado funciona.",
  },
  "memoria-2": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 2 de las 6 pares del tablero.",
  },
  "memoria-3": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 3 de las 6 pares del tablero.",
  },
  "memoria-4": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 4 de las 6 pares del tablero.",
  },
  "memoria-5": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 5 de las 6 pares del tablero.",
  },
  "memoria-6": {
    recommended: "SVG (ideal) o PNG cuadrado 1024×1024 fondo transparente",
    tip: "Imagen 6 de las 6 pares del tablero.",
  },
};

export function AssetUploader({
  kind,
  currentUrl,
  onUploaded,
  onClear,
  showThumbnail = true,
}: {
  kind: AssetKind;
  currentUrl: string | null;
  onUploaded: (url: string) => void;
  onClear: () => void;
  // Cuando es false NO renderiza el thumbnail grande (solo botones +
  // recomendacion). Lo usa la card "Logo del shopping": ahi el preview vivo
  // (LogoPreview) ya muestra la imagen al ancho real, asi que el thumbnail
  // estatico del uploader seria una segunda imagen redundante.
  showThumbnail?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reorg UX 2026-06: la recomendación (dimensiones + peso) es una línea
  // permanente compacta bajo los botones — el dato accionable cuando se
  // elige el archivo, sin un click de revelado.
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleUpload(file: File) {
    setError(null);
    // Validación client-side ANTES de mandar al server. El server
    // igual valida — esto solo evita un round-trip molesto al usuario
    // que sube una imagen pesada y se entera 5s después.
    const acceptedTypes = ASSET_ACCEPTED.split(",");
    if (!acceptedTypes.includes(file.type)) {
      setError(
        `Tipo no permitido: ${file.type || "desconocido"}. Solo PNG, JPG, WebP o SVG.`,
      );
      return;
    }
    if (file.size > ASSET_MAX_MB * 1024 * 1024) {
      setError(
        `Archivo muy grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo ${ASSET_MAX_MB} MB. Comprimila en tinypng.com o squoosh.app antes de subir.`,
      );
      return;
    }
    setUploading(true);
    try {
      const result = await superUploadAsset(file, kind);
      onUploaded(result.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept={ASSET_ACCEPTED}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
          // Reset para permitir re-subir el mismo archivo si quiso
          // corregir algo (sin esto, onChange no dispara la segunda vez).
          e.target.value = "";
        }}
        className="hidden"
      />

      {/* Thumbnail GRANDE arriba — es lo que el user quiere ver
          inmediatamente, no el copy de ayuda. object-contain en lugar
          de cover para que SVG/PNG con relación distinta a 1:1 (ej:
          banderas 4:3) no se recorten. Si todavía no se subió nada,
          mostramos un placeholder centrado para que la card tenga
          tamaño consistente con las que ya tienen imagen.
          showThumbnail=false lo oculta cuando hay un preview externo (ej la
          card del logo usa LogoPreview vivo, no necesita esta segunda imagen). */}
      {showThumbnail && (
        <div className="mb-3 flex h-32 items-center justify-center overflow-hidden rounded-lg bg-black/30">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="preview"
              className="max-h-full max-w-full object-contain p-2"
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-xs opacity-40">
              <ImageIcon className="h-8 w-8" />
              <span>Sin imagen</span>
            </div>
          )}
        </div>
      )}

      {/* LAYOUT_ROTO fix: en columnas angostas (~260px, grid de memoria a
          3 cols) el botón primario + "Quitar" se encimaban. Ahora el
          primario lleva min-w-0 + truncate y crece (flex-1); "Quitar" es
          icono-only con shrink-0 para que nunca se solape ni baje de
          línea. flex-nowrap mantiene ambos en la misma fila. */}
      <div className="flex flex-nowrap items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20 disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate">{currentUrl ? "Cambiar" : "Subir"}</span>
        </button>
        {currentUrl && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Quitar imagen"
            title="Quitar imagen"
            className="flex shrink-0 items-center justify-center rounded-lg bg-red-500/15 p-2 text-red-300 hover:bg-red-500/25"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {/* Sin "?" de recomendación acá: rompía la estética pegado al botón y
            había demasiados. La ayuda de cada card (el "?" del título) alcanza. */}
      </div>

      {error && (
        <div className="mt-2 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
